import { useAuth } from "../../../contexts/AuthContext";
import { AuthGuard } from "../../auth/components/AuthGuard";
import { Header } from "../../landing/components/Header";
import { EventDetailCard } from "../components/EventDetailCard";
import { AuthModal } from "../../auth/components/AuthModal";
import { useParams, useSearchParams } from "react-router-dom";
import { useEffect, useMemo, useState } from "react";
import { Event } from "../../../services/EventService";
import { Helmet } from 'react-helmet-async'

const EventDetailPage = () => {
  // Decodificar el valor que viene en la URL
  const { eventId: rawParam } = useParams<{ eventId: string }>();
  const eventId = rawParam ? decodeURIComponent(rawParam) : undefined;
  const [searchParams] = useSearchParams();
  const { isLoading: isAuthLoading, isAuthenticated, isInitialized } = useAuth();
  const [event, setEvent] = useState<any | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showAuthModal, setShowAuthModal] = useState(false);
  
  const siteUrl = typeof window !== 'undefined' ? window.location.origin : 'https://cap.com';
  const pageUrl = typeof window !== 'undefined' ? window.location.href : 'https://cap.com';

  const seo = useMemo(() => {
    if (!event) return null;
    const title = `${event.title} | Punta Tech Meetup`;
    const description = (event.description || event.short_description || '').slice(0, 160) || 'Evento en la comunidad Punta Tech Meetup';
    const image = event.image_url || `${siteUrl}/icon.ico`;
    const startISO = event.start_date ? `${event.start_date}T${event.start_time || '00:00:00'}` : undefined;
    const endISO = event.end_date ? `${event.end_date}T${event.end_time || '00:00:00'}` : undefined;
    const organizerName = (Array.isArray(event.hosts) && event.hosts.length > 0)
      ? (event.hosts.find((h: any) => h.is_main_host)?.name || event.hosts[0]?.name)
      : undefined;

    const jsonLd = {
      '@context': 'https://schema.org',
      '@type': 'Event',
      name: event.title,
      description: event.description || undefined,
      startDate: startISO,
      endDate: endISO,
      eventAttendanceMode: event.modality === 'online' ? 'https://schema.org/OnlineEventAttendanceMode' : 'https://schema.org/OfflineEventAttendanceMode',
      eventStatus: 'https://schema.org/EventScheduled',
      image: image ? [image] : undefined,
      location: event.location ? {
        '@type': 'Place',
        name: event.location,
      } : undefined,
      organizer: organizerName ? {
        '@type': 'Organization',
        name: organizerName
      } : undefined,
      url: pageUrl
    };

    return { title, description, image, jsonLd };
  }, [event, pageUrl, siteUrl]);
  
  useEffect(() => {
    const fetchEventData = async () => {
      console.log("Fetching event data for:", eventId);
      if (!eventId) {
        setError("Event not found");
        setIsLoading(false);
        return;
      }

      try {
        setIsLoading(true);
        const result = await Event.getBySlug(eventId);
        console.log(result);
        const { data, error } = result;
        
        if (error) {
          console.error("Error fetching event:", error);
          setError("Failed to load event details");
        } else {
          setEvent(data);
        }
      } catch (e) {
        console.error("Exception fetching event:", e);
        setError("An unexpected error occurred");
      } finally {
        setIsLoading(false);
      }
    };

    fetchEventData();
  }, [eventId, searchParams]);
    
  return (
    <>
      {seo && (
        <Helmet>
          <title>{seo.title}</title>
          <meta name="description" content={seo.description} />
          <link rel="canonical" href={pageUrl} />
          <meta property="og:type" content="article" />
          <meta property="og:title" content={seo.title} />
          <meta property="og:description" content={seo.description} />
          <meta property="og:url" content={pageUrl} />
          <meta property="og:image" content={seo.image} />
          <meta name="twitter:card" content="summary_large_image" />
          <meta name="twitter:title" content={seo.title} />
          <meta name="twitter:description" content={seo.description} />
          <meta name="twitter:image" content={seo.image} />
          <script type="application/ld+json">{JSON.stringify(seo.jsonLd)}</script>
        </Helmet>
      )}

      <AuthGuard
      requireAuth={false} 
      isLoading={isAuthLoading} 
      isAuthenticated={isAuthenticated} 
      isInitialized={isInitialized}
    >
      <div className="min-h-screen bg-gray-50">
        {/* Gradient Background Section */}
        <div className="relative overflow-hidden hero-gradient text-white pb-32">
          <div className="absolute inset-0 pointer-events-none">
            <div className="absolute -top-24 -left-24 w-[24rem] h-[24rem] bg-white/25 blur-[180px]" />
            <div className="absolute -bottom-20 -right-32 w-[28rem] h-[28rem] bg-[#ffe619]/25 blur-[220px]" />
          </div>
          <Header theme="dark" onSignInClick={() => setShowAuthModal(true)} />
          {/* Espacio para la superposición de la tarjeta */}
          <div className="relative" style={{height: '27rem'}}></div>
        </div>
        
        {/* Event Detail Card - Positioned to overlap gradient and white sections */}
        <div className="relative px-4" style={{ marginTop: '-27rem' }}>
          <div className="max-w-5xl mx-auto">
            {isLoading ? (
              <div className="bg-white rounded-lg shadow-xl p-8 flex justify-center items-center h-96">
                <div className="text-center">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
                  <p className="text-gray-500">Cargando detalles del evento...</p>
                </div>
              </div>
            ) : error ? (
              <div className="bg-white rounded-lg shadow-xl p-8">
                <div className="text-center text-red-500">
                  <h2 className="text-xl font-semibold mb-2">Error</h2>
                  <p>{error}</p>
                </div>
              </div>
            ) : event ? (
              <EventDetailCard event={event} />
            ) : (
              <div className="bg-white rounded-lg shadow-xl p-8">
                <div className="text-center text-gray-500">
                  <h2 className="text-xl font-semibold mb-2">Evento no encontrado</h2>
                  <p>No se pudo encontrar el evento solicitado.</p>
                </div>
              </div>
            )}
          </div>
        </div>
        
        {/* Main content area */}
        <div className="px-4 py-8">
          {/* Additional content will go here */}
        </div>

        {/* Auth Modal */}
        <AuthModal
          isOpen={showAuthModal}
          onClose={() => setShowAuthModal(false)}
          skipRedirect={true}
          onSuccess={() => {
            setShowAuthModal(false)
            // Optionally refresh the page or update user state
            window.location.reload()
          }}
        />
      </div>
    </AuthGuard>
    </>
  );
}

EventDetailPage.displayName = "Punta Tech Meetup - Detalle del evento";
export default EventDetailPage;
